import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../../services/api';

export const ORDER_CHANGED = 'noke-order-changed';
export const ORDER_PROGRESS_UPDATED = 'noke-order-progress-updated';
export const notifyOrderChanged = () => window.dispatchEvent(new Event(ORDER_CHANGED));
export type WorkflowAction = 'arrive' | 'dispatch' | 'receive_store' | 'display' | 'cancel_remainder';
export interface OrderWorkflowTask {
  line_id: number; batch_id: number; variant_id: number; sku: string;
  title?: string; image_url?: string; color: string; size: string; qty: number;
  received_qty?: number; cancelled_qty?: number; remaining_qty: number;
  pending_office_qty: number; pending_transit_qty: number; pending_display_qty: number;
  received_office_qty: number; dispatched_qty: number; received_store_qty: number; displayed_qty: number;
  legacy_receipt?: boolean;
  history?: Array<{ action: string; qty: number; created_at: string; created_by: string; location?: string }>;
}
export interface WorkflowRequest {
  client_ref: string; line_id: number; action: WorkflowAction; qty: number;
}
const errorText = (error: any) => error?.response?.data?.error || error?.response?.data?.message || error?.message || '無法取得訂單進度';
const storageKey = 'noke-order-workflow-pending';
const loadPending = (): WorkflowRequest | null => {
  try { return JSON.parse(sessionStorage.getItem(storageKey) || 'null'); } catch { return null; }
};

/** A failed response never permits a different mutation under the same request identity. */
export function useOrderWorkflow() {
  const [tasks, setTasks] = useState<OrderWorkflowTask[]>([]);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<WorkflowRequest | null>(loadPending);
  const inflight = useRef(false);
  const sequence = useRef(0);
  const lastProgress = useRef<string | null>(null);
  const refresh = useCallback(async () => {
    const ticket = ++sequence.current;
    try {
      const { data } = await api.get('/api/noke/restock-v2/order-tasks', { params: { include_completed: true, limit: 500 } });
      if (ticket !== sequence.current) return;
      setTasks(Array.isArray(data.tasks) ? data.tasks : []);
      const signature = JSON.stringify((Array.isArray(data.tasks) ? data.tasks : []).map((t: OrderWorkflowTask) =>
        [t.line_id, t.remaining_qty, t.pending_office_qty, t.pending_transit_qty, t.pending_display_qty, t.displayed_qty, t.cancelled_qty]));
      const changed = lastProgress.current !== null && lastProgress.current !== signature;
      lastProgress.current = signature;
      // Another device may have received this parcel. Refresh the planner only on a real
      // quantity change, using a separate event so polling cannot trigger a read loop.
      if (changed) window.dispatchEvent(new Event(ORDER_PROGRESS_UPDATED));
      setReady(data.ready === true);
      setError(data.ready === false ? (data.note || data.message || '訂單流程尚未啟用，請完成資料庫更新後再操作。')
        : data.truncated ? '目前僅顯示前 500 筆訂單明細，清單尚未全部載入。' : '');
    } catch (e) {
      if (ticket === sequence.current) setError(errorText(e));
    } finally { if (ticket === sequence.current) setLoading(false); }
  }, []);
  useEffect(() => {
    refresh();
    const refreshVisible = () => { if (document.visibilityState !== 'hidden') refresh(); };
    const timer = window.setInterval(refreshVisible, 30000);
    window.addEventListener('focus', refreshVisible);
    window.addEventListener(ORDER_CHANGED, refreshVisible);
    return () => {
      sequence.current += 1;
      window.clearInterval(timer);
      window.removeEventListener('focus', refreshVisible);
      window.removeEventListener(ORDER_CHANGED, refreshVisible);
    };
  }, [refresh]);
  const act = useCallback(async (task: OrderWorkflowTask, action: WorkflowAction, qty: number) => {
    if (inflight.current) return false;
    const saved = pending || loadPending();
    if (saved && (saved.line_id !== task.line_id || saved.action !== action)) {
      setError('上一次操作尚未確認，請先重試原操作。'); return false;
    }
    if (!saved && (!Number.isInteger(qty) || qty <= 0)) return false;
    const request = saved || {
      client_ref: `workflow-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      line_id: task.line_id, action, qty,
    };
    // Persist before sending: a page reload after a timeout must retry the original request.
    try { sessionStorage.setItem(storageKey, JSON.stringify(request)); }
    catch { setError('無法保存操作，請確認瀏覽器儲存空間後重試。'); return false; }
    setPending(request); setBusy(true); inflight.current = true; setError('');
    try {
      await api.post('/api/noke/restock-v2/order-task', request);
      sessionStorage.removeItem(storageKey); setPending(null);
      notifyOrderChanged();
      return true;
    } catch (e: any) {
      // 4xx is a definitive refusal; network/5xx is an unknown outcome and must retain identity.
      if (e?.response?.status >= 400 && e.response.status < 500 && ![408, 429].includes(e.response.status)) {
        sessionStorage.removeItem(storageKey); setPending(null);
      }
      setError(errorText(e)); return false;
    } finally { setBusy(false); inflight.current = false; }
  }, [pending]);
  return { tasks, ready, loading, error, busy, pending, refresh, act };
}
export type OrderWorkflow = ReturnType<typeof useOrderWorkflow>;
